import { useState, useCallback, useContext, createContext } from 'react';

// ─── Types ─────────────────────────────────────────────────────────────────
type ToastType = 'error' | 'success' | 'warning' | 'info';

interface Toast {
  id:      number;
  message: string;
  type:    ToastType;
}

interface ToastContextValue {
  toasts:       Toast[];
  showToast:    (message: string, type?: ToastType) => void;
  dismissToast: (id: number) => void;
}

// ─── Context ───────────────────────────────────────────────────────────────
export const ToastContext = createContext<ToastContextValue>({
  toasts:       [],
  showToast:    () => {},
  dismissToast: () => {},
});

// ─── Provider ──────────────────────────────────────────────────────────────
export function ToastProvider({ children }: { children: React.ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);
  let nextId = Date.now();

  const showToast = useCallback((message: string, type: ToastType = 'error') => {
    const id = nextId++;
    setToasts(prev => [...prev, { id, message, type }]);
    setTimeout(() => {
      setToasts(prev => prev.filter(t => t.id !== id));
    }, 5000);
  }, []);

  const dismissToast = useCallback((id: number) => {
    setToasts(prev => prev.filter(t => t.id !== id));
  }, []);

  return (
    <ToastContext.Provider value={{ toasts, showToast, dismissToast }}>
      {children}
      <ToastContainer toasts={toasts} onDismiss={dismissToast} />
    </ToastContext.Provider>
  );
}

// ─── Hook ──────────────────────────────────────────────────────────────────
export function useToast() {
  return useContext(ToastContext);
}

// ─── UI Component ──────────────────────────────────────────────────────────
const COLORS: Record<ToastType, { bg: string; border: string; icon: string }> = {
  error:   { bg: 'var(--danger)',  border: '#b91c1c', icon: '✕' },
  success: { bg: 'var(--success)', border: '#15803d', icon: '✓' },
  warning: { bg: 'var(--warning)', border: '#b45309', icon: '!' },
  info:    { bg: 'var(--accent)',  border: '#1d4ed8', icon: 'i' },
};

function ToastContainer({ toasts, onDismiss }: { toasts: Toast[]; onDismiss: (id: number) => void }) {
  if (toasts.length === 0) return null;

  return (
    <div
      role="region"
      aria-label="Notificaciones"
      aria-live="polite"
      style={{
        position:      'fixed',
        bottom:        '1.5rem',
        right:         '1.5rem',
        zIndex:        9999,
        display:       'flex',
        flexDirection: 'column',
        gap:           '0.5rem',
        maxWidth:      '360px',
        width:         '100%',
      }}
    >
      {toasts.map(t => {
        const c = COLORS[t.type];
        return (
          <div
            key={t.id}
            role="alert"
            style={{
              display:       'flex',
              alignItems:    'flex-start',
              gap:           '0.75rem',
              padding:       '0.75rem 1rem',
              background:    'var(--surface2)',
              border:        `1px solid ${c.border}`,
              borderLeft:    `3px solid ${c.bg}`,
              borderRadius:  'var(--radius-md)',
              color:         'var(--text)',
              fontSize:      '0.875rem',
              lineHeight:    '1.4',
              boxShadow:     '0 4px 12px rgba(0,0,0,0.4)',
              animation:     'fade-in 0.2s ease',
            }}
          >
            <span
              aria-hidden="true"
              style={{
                flexShrink:    0,
                width:         '18px',
                height:        '18px',
                borderRadius:  '50%',
                background:    c.bg,
                color:         '#fff',
                display:       'flex',
                alignItems:    'center',
                justifyContent:'center',
                fontSize:      '11px',
                fontWeight:    700,
                marginTop:     '1px',
              }}
            >
              {c.icon}
            </span>
            <span style={{ flex: 1 }}>{t.message}</span>
            <button
              onClick={() => onDismiss(t.id)}
              aria-label="Cerrar notificación"
              style={{
                flexShrink:    0,
                background:    'none',
                border:        'none',
                color:         'var(--muted)',
                cursor:        'pointer',
                padding:       '2px',
                fontSize:      '14px',
                lineHeight:    1,
              }}
            >
              ×
            </button>
          </div>
        );
      })}
    </div>
  );
}
